import React from 'react';
import { Link } from 'react-router-dom';
import MaskedInput from 'react-text-mask';

import { Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';

import Card from '@/components/common/Card';
import ColorButton from '@/components/common/elements/ColorButton';
import permissionsImage from '@/components/common/icons/document-circle.svg';
import userCircleImage from '@/components/common/icons/user-circle.svg';
import userImage from '@/components/common/icons/user-permission.svg';
import Spinner from '@/components/common/Spinner';
import { USERNAME_MASK } from '@/helpers/mask';
import urlPrefix from '@/helpers/url-prefix';

const UserFormComponent = ({
  userId,
  features,
  initialValues,
  isUserBusy,
  isFeaturesBusy,
  action,
  intl,
  handleSubmit,
  cpfMask,
  phoneMask,
  cellphoneMask,
  cellphoneMaskLength,
}) => {
  const inputClasses =
    'w-full h-[3em] px-[0.5em] border border-[#e3e3e3] rounded-[3px] bg-white font-[\'Open_Sans\'] !text-[0.875em] font-semibold text-[#919191] transition-colors focus:outline-none focus:border-[#4d8fca] placeholder:font-normal placeholder:text-[#b1b1b1] disabled:!bg-[#efefef] disabled:cursor-not-allowed';
  const labelClasses = 'block text-[0.75em] font-bold uppercase text-[#393939] mb-0';
  const fieldClasses = 'flex flex-col mt-[1.25em] px-[3.46em] text-[0.93875em] font-medium text-[#919191]';

  if (isFeaturesBusy || isUserBusy) {
    return (
      <div className="flex flex-col p-[24.5px_22.75px]">
        <div className="flex justify-center items-center py-[24.5px]">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-[1.5em_1em]">
      <Card className="fade-in">
        <h2 className="flex items-center mb-[1.4em] pb-[1.4em] border-b border-[#eee] !text-[1.25em] !font-bold !leading-[1.3] text-[#393939]">
          <Link
            to={action === 'edit' ? `${urlPrefix('users')}/${userId}` : urlPrefix('users')}
            className="flex items-center justify-center w-[20px] h-[24px] mr-[8.75px] no-underline"
          >
            <div className="w-[14px] h-[14px] border-l-2 border-b-2 border-[#393939] rotate-45" />
          </Link>

          <div className="w-[28px] h-[28px] flex items-center justify-center flex-shrink-0 mr-[8.75px]">
            <img src={userImage} alt={intl.formatMessage({ id: 'USER' })} className="max-w-[28px] h-[28px]" />
          </div>

          <span className="text-[1em] font-bold leading-[1.3] text-[#393939]">
            {action === 'edit'
              ? intl.formatMessage({ id: 'USER_EDITION' })
              : intl.formatMessage({ id: 'USER_REGISTRATION' })}
          </span>
        </h2>

        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ isSubmitting }) => (
            <Form>
              <h3 className="flex items-center mb-0 mt-0 !text-[0.93875em] !font-bold !leading-[1.1] text-[#393939]">
                <div className="w-[3.46em] flex items-center justify-center flex-shrink-0">
                  <img src={userCircleImage} alt="" className="h-[24px] w-auto" />
                </div>
                <span className="text-[1em] font-bold leading-[1.1] text-[#393939]">
                  {intl.formatMessage({ id: 'BASIC_DATA' })}
                </span>
              </h3>

              <div className={fieldClasses}>
                <label htmlFor="name" className={labelClasses}>
                  {intl.formatMessage({ id: 'fullName' })}*
                </label>
                <Field name="name">
                  {({ field }) => (
                    <input {...field} type="text" id="name" data-cy="name" required className={inputClasses} />
                  )}
                </Field>
              </div>

              <div className={fieldClasses}>
                <label htmlFor="cpf" className={labelClasses}>
                  {intl.formatMessage({ id: 'CPF' })}
                </label>
                <Field name="cpf">
                  {({ field }) => (
                    <MaskedInput
                      {...field}
                      type="text"
                      id="cpf"
                      data-cy="cpf"
                      guide={false}
                      mask={cpfMask}
                      className={inputClasses}
                    />
                  )}
                </Field>
              </div>

              <div className={fieldClasses}>
                <label htmlFor="username" className={labelClasses}>
                  {intl.formatMessage({ id: 'USER' })}*
                </label>
                <Field name="username">
                  {({ field }) => (
                    <MaskedInput
                      {...field}
                      type="text"
                      id="username"
                      data-cy="username"
                      guide={false}
                      mask={USERNAME_MASK}
                      disabled={action === 'edit'}
                      required
                      className={inputClasses}
                    />
                  )}
                </Field>
              </div>

              <div className={fieldClasses}>
                <label htmlFor="email" className={labelClasses}>
                  {intl.formatMessage({ id: 'EMAIL' })}*
                </label>
                <Field
                  type="email"
                  name="email"
                  id="email"
                  data-cy="email"
                  disabled={action === 'edit'}
                  required
                  className={inputClasses}
                />
              </div>

              <div className={fieldClasses}>
                <label htmlFor="phone" className={labelClasses}>
                  {intl.formatMessage({ id: 'CONTACT' })}
                </label>
                <Field name="phone">
                  {({ field }) => (
                    <MaskedInput
                      {...field}
                      type="text"
                      id="phone"
                      data-cy="phone"
                      guide={false}
                      mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
                      className={inputClasses}
                    />
                  )}
                </Field>
              </div>

              <h3 className="flex items-center mb-0 !mt-[1.5em] !text-[0.93875em] !font-bold !leading-[1.1] text-[#393939]">
                <div className="w-[3.46em] flex items-center justify-center flex-shrink-0">
                  <img src={permissionsImage} alt="" className="h-[24px] w-auto" />
                </div>
                <span className="text-[1em] font-bold leading-[1.1] text-[#393939]">
                  {intl.formatMessage({ id: 'PERMISSIONS' })}
                </span>
              </h3>

              <div className="px-[3.46em] mt-[1.25em] text-[0.93875em] font-medium text-[#919191]">
                <ul className="list-none flex justify-between flex-wrap m-0 p-0">
                  {features.map(feature => (
                    <li
                      key={feature.description}
                      className="flex items-center w-[calc(50%-5px)] h-[24px] leading-none break-all"
                    >
                      <label className="flex items-center m-0 whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer text-[0.75em] font-bold uppercase text-[#393939]">
                        <Field
                          name="features"
                          data-cy={`feature-${feature.description}`}
                          type="checkbox"
                          value={`${feature.id}`}
                          className="!mr-[0.33rem] align-middle flex-shrink-0"
                        />
                        <span>
                          {intl.formatMessage({ id: `PERMISSION.${feature.description}` })}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex justify-center items-center mt-[1.75em] pt-[1.75em] border-t border-[#eee]">
                <ColorButton type="submit" color="green" isBusy={isSubmitting}>
                  {intl.formatMessage({ id: 'SAVE' })}
                </ColorButton>
              </div>
            </Form>
          )}
        </Formik>
      </Card>
    </div>
  );
};

UserFormComponent.propTypes = {
  userId: PropTypes.string,
  features: PropTypes.array.isRequired,
  initialValues: PropTypes.object.isRequired,
  isUserBusy: PropTypes.bool.isRequired,
  isFeaturesBusy: PropTypes.bool.isRequired,
  action: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  cpfMask: PropTypes.array.isRequired,
  phoneMask: PropTypes.array.isRequired,
  cellphoneMask: PropTypes.array.isRequired,
  cellphoneMaskLength: PropTypes.number.isRequired,
};

UserFormComponent.defaultProps = {
  userId: null,
};

export default UserFormComponent;
